import { cities, countries, nigerianStates } from '@/lib/constants';

type SelectOption = { label: string; value: string };

export interface BusinessAddressFormFields {
  businessAddress: string;
  state: string;
  customState?: string;
  lga: string;
  customLga?: string;
  country: string;
  customCountry?: string;
}

function mapStoredValueToSelect(
  value: string | null | undefined,
  options: SelectOption[]
): { value: string; custom?: string } {
  if (!value?.trim()) return { value: '' };

  const normalized = value.trim().toLowerCase();
  const match = options.find(
    (option) =>
      option.value === normalized || option.label.toLowerCase() === normalized
  );

  if (match) return { value: match.value };
  return { value: 'others', custom: value.trim() };
}

export function resolveBusinessAddressForApi(data: BusinessAddressFormFields) {
  const businessAddress = data.businessAddress.trim();
  const state =
    data.state === 'others' ? (data.customState?.trim() ?? '') : data.state;
  const lga = data.lga === 'others' ? (data.customLga?.trim() ?? '') : data.lga;
  const country =
    data.country === 'others'
      ? (data.customCountry?.trim() ?? '')
      : data.country;

  return {
    businessAddress,
    state,
    lga,
    country,
    location: [businessAddress, lga, state, country].filter(Boolean).join(', '),
  };
}

function parseLegacyLocation(location?: string | null): {
  businessAddress?: string;
  state?: string;
  country?: string;
} {
  if (!location?.trim()) return {};

  const trimmed = location.trim();
  const parts = trimmed
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length >= 2) {
    return {
      businessAddress: trimmed,
      state: parts[0],
      country: parts[parts.length - 1],
    };
  }

  return { businessAddress: trimmed };
}

export function mapBusinessToAddressFormValues(business: {
  businessAddress?: string | null;
  location?: string | null;
  state?: string | null;
  lga?: string | null;
  country?: string | null;
  submittedData?: {
    businessAddress?: string | null;
    state?: string | null;
    lga?: string | null;
    country?: string | null;
  } | null;
}): BusinessAddressFormFields {
  const hasStructuredAddress = Boolean(
    business.businessAddress?.trim() ||
      business.state?.trim() ||
      business.lga?.trim() ||
      business.country?.trim() ||
      business.submittedData?.businessAddress?.trim() ||
      business.submittedData?.state?.trim() ||
      business.submittedData?.lga?.trim() ||
      business.submittedData?.country?.trim()
  );

  const legacyLocation = !hasStructuredAddress
    ? parseLegacyLocation(business.location)
    : {};

  const businessAddress =
    business.businessAddress ??
    business.submittedData?.businessAddress ??
    legacyLocation.businessAddress ??
    business.location ??
    '';

  const stateMapping = mapStoredValueToSelect(
    business.state ?? business.submittedData?.state ?? legacyLocation.state,
    nigerianStates
  );
  const lgaMapping = mapStoredValueToSelect(
    business.lga ?? business.submittedData?.lga,
    cities
  );
  const countryMapping = mapStoredValueToSelect(
    business.country ??
      business.submittedData?.country ??
      legacyLocation.country,
    countries
  );

  let country = countryMapping.value;
  const customCountry = countryMapping.custom;

  if (!country && stateMapping.value && stateMapping.value !== 'others') {
    country = 'nigeria';
  }

  return {
    businessAddress,
    state: stateMapping.value,
    customState: stateMapping.custom,
    lga: lgaMapping.value,
    customLga: lgaMapping.custom,
    country,
    customCountry,
  };
}

export function mapBusinessToKybAddressPrefill(business: {
  name?: string;
  category?: string | null;
  description?: string | null;
  businessAddress?: string | null;
  location?: string | null;
  state?: string | null;
  lga?: string | null;
  country?: string | null;
  submittedData?: {
    businessAddress?: string | null;
    state?: string | null;
    lga?: string | null;
    country?: string | null;
    industry?: string | null;
    description?: string | null;
    businessName?: string | null;
  } | null;
}) {
  const address = mapBusinessToAddressFormValues(business);

  return {
    businessType: 'registered' as const,
    businessName: business.submittedData?.businessName ?? business.name ?? '',
    street: address.businessAddress,
    state: address.state,
    customState: address.customState,
    lga: address.lga,
    customLga: address.customLga,
    country: address.country || 'nigeria',
    customCountry: address.customCountry,
    businessIndustry:
      business.submittedData?.industry ?? business.category ?? '',
    businessDescription:
      business.description ?? business.submittedData?.description ?? '',
  };
}
